<script>
  import { createEventDispatcher } from "svelte";

  export let steps = ["Department", "Describe work", "Review", "Saved"];
  // The active step uses a zero-based index.
  export let currentStep = 0;
  export let completed = false;
  export let ariaLabel = "Framework progress";
  export let navigableSteps = [];
  export let disabled = false;
  const dispatch = createEventDispatcher();

  // Reaktiv, sonst rechnet Svelte die Buttons nicht neu, wenn sich navigableSteps aendert (blieben gesperrt)
  $: isStepDisabled = (index) => disabled || !navigableSteps.includes(index);

  $: disabledReason = (index) => {
    if (!isStepDisabled(index)) return undefined;
    if (disabled) return "Please wait until the current action is finished.";
    if (completed) return "The framework has already been saved.";
    if (index === currentStep) return "You are currently on this step.";
    if (index > currentStep) return "Complete the current step before opening this step.";
    return "This step cannot be opened from the current state.";
  };
</script>

<div class="framework-progress-container">
  {#if steps.length}
    <ol class="framework-progress" aria-label={ariaLabel}>
      {#each steps as label, index}
        <li class:current={!completed && currentStep === index} class:complete={currentStep > index || completed} aria-current={!completed && currentStep === index ? "step" : undefined} title={disabledReason(index)}>
          <button type="button" class="step-link" disabled={isStepDisabled(index)} on:click={() => dispatch("navigate", index)}>
          <span class="step-circle">
            {#if currentStep > index || completed}
              <img src="/img/assistant/icons/check.svg" alt="Completed" />
            {:else}
              <span class="step-number">{index + 1}</span>
            {/if}
          </span>{label}
          </button>
        </li>
      {/each}
    </ol>
    <p class="framework-progress-compact" aria-label={`Step ${currentStep + 1} of ${steps.length}: ${steps[currentStep]}`}>
      {#if navigableSteps.includes(0)}
        <button type="button" disabled={disabled} on:click={() => dispatch("navigate", 0)}>{steps[0]}</button>
      {/if}
      {currentStep + 1} / {steps.length}<span> · {steps[currentStep]}</span>
    </p>
  {/if}
</div>

<style>
  .framework-progress-container {
    width: 100%;
    container-type: inline-size;
  }

  .framework-progress {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 1.25rem;
    padding: 0;
    margin: 0;
    list-style: none;
    color: var(--pax-grey);
    font-size: inherit;
    white-space: nowrap;
  }

  .framework-progress li {
    display: flex;
    align-items: center;
    gap: .4rem;
    font-size: inherit;
  }

  .framework-progress li.current,
  .framework-progress li.complete {
    color: var(--pax-black);
  }

  .framework-progress li.current {
    font-weight: 600;
  }

  .step-link {
    display: flex;
    align-items: center;
    gap: .4rem;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    opacity: 1;
  }

  .step-link:not(:disabled),
  .framework-progress-compact button:not(:disabled) {
    cursor: pointer;
  }

  .step-link:not(:disabled):hover {
    text-decoration: underline;
  }

  .step-link:disabled {
    cursor: not-allowed;
  }

  .framework-progress li.current .step-link:disabled {
    cursor: default;
  }

  .step-link:focus-visible,
  .framework-progress-compact button:focus-visible {
    outline: 2px solid var(--pax-orange);
    outline-offset: .25rem;
  }

  .framework-progress .step-circle {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 1.25rem;
    box-sizing: border-box;
    width: 1.25rem;
    height: 1.25rem;
    font-size: .75rem;
    font-weight: 400;
    line-height: 1;
    border: 1px solid currentColor;
    border-radius: 50%;
  }

  .framework-progress li.current .step-circle {
    border: 1px solid var(--pax-black);
    background: var(--pax-white);
  }

  .framework-progress li.current .step-number {
    font-weight: 400;
  }

  .framework-progress li.complete .step-circle {
    border-color: var(--pax-black);
    background: var(--pax-black);
    color: var(--pax-white);
  }

  .step-number {
    display: block;
    line-height: 1;
    text-align: center;
    font-variant-numeric: lining-nums proportional-nums;
    /* Center the visible numeral rather than the font's ascent/descent space. */
    text-box-trim: trim-both;
    text-box-edge: cap alphabetic;
  }

  .framework-progress img {
    display: block;
    width: .75rem;
    height: .5625rem;
    object-fit: contain;
  }

  .framework-progress-compact {
    display: none;
    margin: 0;
    color: var(--pax-black);
    font-size: inherit;
    text-align: right;
    white-space: nowrap;
  }

  .framework-progress-compact button {
    margin-right: .75rem;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-decoration: underline;
  }

  @container (max-width: 36rem) {
    .framework-progress {
      display: none;
    }

    .framework-progress-compact {
      display: block;
    }
  }

  @container (max-width: 14rem) {
    .framework-progress-compact span {
      display: none;
    }
  }

</style>
